
import React, { useState } from 'react';
import { MemberModel, StudentCounselModel } from '../../../../models';
import { Search, Filter, CheckCircle2, Clock, Calendar, Headphones, MessageSquare } from 'lucide-react';
import CounselList from './CounselList';
import CounselView from './CounselView';
import AdminPageHeader from '../../frame/RightPanelHeader';

interface CounselRootProps {
    members: MemberModel[];
    counsels: StudentCounselModel[];
}

const CounselRoot: React.FC<CounselRootProps> = ({ members, counsels }) => {
    // Selection State
    const [selectedCounsel, setSelectedCounsel] = useState<StudentCounselModel | null>(null);

    // Search State
    const [searchTerm, setSearchTerm] = useState('');

    // Counsel Filters (Input)
    const [tagFilter, setTagFilter] = useState<string>('all');
    const [replyFilter, setReplyFilter] = useState<'all' | 'replied' | 'waiting'>('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Applied Filters for Manual Trigger
    const [appliedStartDate, setAppliedStartDate] = useState('');
    const [appliedEndDate, setAppliedEndDate] = useState('');
    const [appliedReplyFilter, setAppliedReplyFilter] = useState<'all' | 'replied' | 'waiting'>('all');
    const [appliedTagFilter, setAppliedTagFilter] = useState<string>('all');

    const handleApplyFilters = () => {
        setAppliedStartDate(startDate);
        setAppliedEndDate(endDate);
        setAppliedReplyFilter(replyFilter);
        setAppliedTagFilter(tagFilter);
    };

    const getMemberName = (memberId: string) => {
        const member = members.find(m => m.id === memberId);
        return member ? member.name : '';
    };

    // --- Filter Logic for Counsels ---
    const uniqueTags = Array.from(new Set(counsels.map(c => c.tag).filter(Boolean)));

    const filteredCounsels = counsels.filter(c => {
        const memberName = getMemberName(c.memberId);
        const matchesSearch =
            c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
            memberName.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesTag = appliedTagFilter === 'all' || c.tag === appliedTagFilter;

        // Robust Reply Check
        const hasReply = c.reply !== undefined && c.reply !== null && c.reply.trim().length > 0;
        const matchesReply =
            appliedReplyFilter === 'all' ||
            (appliedReplyFilter === 'replied' && hasReply) ||
            (appliedReplyFilter === 'waiting' && !hasReply);

        // Filter by date (Timestamp) - Robust Manual Parsing
        let startTs = 0;
        if (appliedStartDate) {
            const [y, m, d] = appliedStartDate.split('-').map(Number);
            startTs = new Date(y, m - 1, d, 0, 0, 0).getTime();
        }

        let endTs = Number.MAX_SAFE_INTEGER;
        if (appliedEndDate) {
            const [y, m, d] = appliedEndDate.split('-').map(Number);
            endTs = new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
        }

        const counselDateTs = typeof c.date === 'number' ? c.date : new Date(c.date).getTime();
        const matchesDate = counselDateTs >= startTs && counselDateTs <= endTs;

        return matchesSearch && matchesTag && matchesReply && matchesDate;
    });

    return (
        <div className="flex flex-col h-full bg-white overflow-hidden w-full">
            <AdminPageHeader
                title="상담 관리"
                icon={Headphones}
                subtitle="학생 상담 내역을 관리합니다"
            />

            {/* Search Bar Area */}
            <div className="px-8 py-5 border-b border-slate-200 bg-white shrink-0 z-10 w-full">
                <div className="flex items-center justify-between mb-4 w-full h-6">
                    <p className="text-sm font-bold text-slate-500">
                        전체 {filteredCounsels.length}건
                    </p>
                </div>

                {/* Filters */}
                <div className="flex gap-3 w-full">
                    {/* Search Input Filter */}
                    <div className="relative flex-1 group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={18} />
                        <input
                            type="text"
                            placeholder="학생 이름, 상담 제목, 내용 검색..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-12 pr-4 py-3 text-sm font-medium outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all shadow-sm"
                        />
                    </div>

                    {/* Date Filters */}
                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 shadow-sm">
                        <Calendar size={16} className="text-slate-400" />
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="bg-transparent text-sm font-medium outline-none w-32 border-none p-0 focus:ring-0 text-slate-600"
                        />
                        <span className="text-slate-400 text-xs">~</span>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="bg-transparent text-sm font-medium outline-none w-32 border-none p-0 focus:ring-0 text-slate-600"
                        />
                    </div>

                    {/* Reply Filter */}
                    <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                            {replyFilter === 'replied' ? <CheckCircle2 size={18} className="text-emerald-500" /> :
                                replyFilter === 'waiting' ? <Clock size={18} className="text-amber-500" /> :
                                    <Filter size={18} />}
                        </div>
                        <select
                            value={replyFilter}
                            onChange={(e) => setReplyFilter(e.target.value as 'all' | 'replied' | 'waiting')}
                            className="bg-slate-50 border border-slate-200 rounded-xl pl-12 pr-10 py-3 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 appearance-none cursor-pointer min-w-[130px] shadow-sm"
                        >
                            <option value="all">전체 상태</option>
                            <option value="replied">답변 완료</option>
                            <option value="waiting">미답변</option>
                        </select>
                    </div>

                    {/* Tag Filter */}
                    <div className="relative">
                        <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <select
                            value={tagFilter}
                            onChange={(e) => setTagFilter(e.target.value)}
                            className="bg-slate-50 border border-slate-200 rounded-xl pl-12 pr-10 py-3 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 appearance-none cursor-pointer min-w-[130px] shadow-sm max-w-[150px]"
                        >
                            <option value="all">전체 태그</option>
                            {uniqueTags.map(tag => (
                                <option key={tag} value={tag}>{tag}</option>
                            ))}
                        </select>
                    </div>

                    {/* Search Button */}
                    <button
                        onClick={handleApplyFilters}
                        className="px-4 py-3 bg-indigo-600 text-white text-sm font-bold rounded-xl shadow-lg shadow-indigo-100 hover:bg-indigo-700 active:scale-95 transition-all flex items-center gap-2"
                    >
                        <Search size={16} />
                        검색
                    </button>
                </div>
            </div>

            {/* Main Content: Split View */}
            <div className="flex-1 flex overflow-hidden">
                {/* Left Panel: List (Fixed Width) */}
                <div className="w-[400px] flex flex-col border-r border-slate-200 bg-slate-50 z-0 flex-shrink-0" style={{ width: '400px' }}>
                    <CounselList
                        counsels={filteredCounsels}
                        members={members}
                        onSelect={setSelectedCounsel}
                        selectedId={selectedCounsel?.id}
                    />
                </div>

                {/* Right Panel: View or Placeholder */}
                <div className="flex-1 bg-white flex flex-col overflow-hidden relative">
                    {selectedCounsel ? (
                        <div className="w-full h-full animate-in fade-in duration-300">
                            <CounselView
                                counsel={selectedCounsel}
                                member={members.find(m => m.id === selectedCounsel.memberId)}
                                onClose={() => setSelectedCounsel(null)}
                                onUpdate={() => { }}
                            />
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-slate-300 select-none">
                            <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mb-6">
                                <MessageSquare size={40} className="text-slate-200" />
                            </div>
                            <p className="text-xl font-bold text-slate-400 mb-2">상담 내역을 선택해주세요</p>
                            <p className="text-sm text-slate-400">좌측 목록에서 항목을 클릭하면 상세 내용을 볼 수 있습니다.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CounselRoot;
